import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClipBoardFormElement from '../form/ClipBoardFormElement';
import Colors from '../utilities/HyColors';
import PropTypes from 'prop-types';
import ExternalLink from '../utilities/ExternalLink';
import ElementHeader from '../form/ElementHeader';
import CheckBox from '../form/CheckBox';
import InputField from '../form/InputField';
import './RecordLink.css';

const formatMS = (totalSeconds) => {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds));
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const RecordLink = ({ to, label, showNotice, startFrom = 0 }) => {
    const { t } = useTranslation();

    const [checked, setChecked] = useState(false);
    const [timestampText, setTimestampText] = useState(formatMS(startFrom));

    useEffect(() => {
        setTimestampText(formatMS(startFrom));
    }, [startFrom]);


    const parsedSeconds = useMemo(() => {
        const raw = (timestampText ?? '').trim();

        if (/^\d+$/.test(raw)) {
            return Number(raw);
        }

        const parts = raw.split(':').map((p) => p.trim());

        if (parts.length === 2) {
            const minutes = Number(parts[0]);
            const seconds = Number(parts[1]);
            if (!Number.isFinite(minutes) || !Number.isFinite(seconds) || seconds < 0 || seconds > 59) return null;
            return minutes * 60 + seconds;
        }

        return null;
    }, [timestampText]);

    const linkWithTimestamp = useMemo(() => {
        if (!checked) return to;

        const seconds = parsedSeconds;
        if (seconds === null || !Number.isFinite(seconds)) return to;

        const joiner = to.includes('?') ? '&' : '?';
        const formatted = formatMS(seconds);

        return `${to}${joiner}t=${formatted}`;
    }, [to, checked, parsedSeconds]);

    const visibleLinkLabel = useMemo(() => {
        if (!checked) return label;

        const seconds = parsedSeconds;
        if (seconds === null || !Number.isFinite(seconds)) {
            return `${label}?t=`;
        }

        return `${label}?t=${formatMS(seconds)}`;
    }, [label, checked, parsedSeconds]);

    const handleTimestampBlur = () => {
        const seconds = parsedSeconds;
        if (seconds === null || !Number.isFinite(seconds)) return;
        setTimestampText(formatMS(seconds));
    };

    if (showNotice) {
        return (
            <>
                <ElementHeader>{t('record_link_private_header')}</ElementHeader>
                <span>{t('record_link_private_notice')}</span>
            </>
        );
    }

    return (
        <ClipBoardFormElement
            label={t('record_link_header')}
            content={linkWithTimestamp}
            buttonAriaLabel={t('record_link_copy_aria')}
        >
            <div className="record-link">
                <ExternalLink
                    to={linkWithTimestamp}
                    label={visibleLinkLabel}
                    fill={Colors.black}
                    height={16}
                    width={16}
                />
            </div>

            <div className="record-link-timestamp-container">
                <div className="checkbox">
                    <CheckBox
                        checked={checked}
                        onChange={(e) => setChecked(Boolean(e?.target?.checked))}
                        label={t('record_link_checkbox_label')}
                        aria-label={t('record_link_checkbox_aria_label')}
                    />
                </div>
                <div className="timestamp-input-field">
                    <InputField
                        type="text"
                        value={timestampText}
                        onChange={(e) => setTimestampText(e?.target?.value ?? '')}
                        onBlur={handleTimestampBlur}
                        message={{}}
                        hideMessage={true}
                        aria-label={t('record_link_header')}
                    />
                </div>
            </div>
        </ClipBoardFormElement>
    );
};

RecordLink.propTypes = {
    to: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    startFrom: PropTypes.number,
};

export default RecordLink;
